import {
	useDashboardStore,
	useFetchFarmerDashboard,
	useFetchSpecialistDashboard,
} from '@features/dashboard/store';
import { useAppbarTitle } from '@hooks/useAppbarTitle';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { usePageTitle } from '@hooks/usePageTitle';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, Button, CircularProgress } from '@mui/material';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useUsersStore } from 'services/users';

export const OfferTypeViewPage = () => {
	const { t } = useTranslation();
	const { navigateWithLanguage } = useLanguageNavigate();
	const { user } = useUsersStore();
	const [expressLoading, setExpressLoading] = useState(false);
	const initialLoadDone = useRef(false);

	const isFarmerPath = user?.role?.toLowerCase() === 'farmer';

	usePageTitle(t('viewRequestsOffers'));

	useEffect(() => {
		localStorage.removeItem('selectedOffer');
		localStorage.removeItem('request_id');
		localStorage.removeItem('category_id');
		// Clean up any previous loading state
		localStorage.removeItem('express_loading');
	}, []);

	const getNavigationPath = (destination: string) => {
		const role = isFarmerPath ? 'farmer' : 'specialist';
		return `/${role}/${destination}`;
	};

	usePageTitle(isFarmerPath ? t('offers') : t('requests'));
	useAppbarTitle(isFarmerPath ? 'offers' : 'requests');

	const { specialistData, farmerData } = useDashboardStore();
	const specialist = specialistData?.data;
	const farmer = farmerData?.data;

	// Always call both hooks to avoid the "rendered more hooks than during the previous render" error
	// but only use the data based on the user role
	const { refresh: refreshFarmer } = useFetchFarmerDashboard();
	const { refresh: refreshSpecialist } = useFetchSpecialistDashboard();

	// Use useEffect to refresh the appropriate data when user changes
	useEffect(() => {
		// Only fetch data if:
		// 1. We have a user
		// 2. Either we haven't loaded data yet OR this is the first time loading this component
		const needsFarmerData = isFarmerPath && (!farmerData || !farmer);
		const needsSpecialistData = !isFarmerPath && (!specialistData || !specialist);
		
		if (user && (needsFarmerData || needsSpecialistData || !initialLoadDone.current)) {
			console.log(`Fetching ${isFarmerPath ? 'farmer' : 'specialist'} dashboard data for view-offers page`);
			
			if (isFarmerPath) {
				refreshFarmer();
			} else {
				refreshSpecialist();
			}
			
			// Mark that we've done the initial load
			initialLoadDone.current = true;
		}
	}, [user, isFarmerPath, refreshFarmer, refreshSpecialist, farmerData, farmer, specialistData, specialist]);

	const analytics = isFarmerPath ? farmer : specialist;
	
	const handleExpressClick = () => {
		setExpressLoading(true);
		// Set loading state in localStorage
		localStorage.setItem('express_loading', 'true');
		// Navigate to express offers
		navigateWithLanguage(getNavigationPath('express/offers'));
	};

	return (
		<div className="h-full flex flex-col justify-center items-center gap-[30px] max-lg:gap-[30px]">
			<h1 className="text-dark text-center roboto-bold text-[24px] leading-[140%] max-sm:text-[18px]">
				{t('viewRequestsOffers')}
			</h1>
			<div className="grid grid-cols-3 gap-4 max-md:!grid-cols-1">
				<div className="flex flex-col gap-[20px] pb-5">
					<div
						className="border-t-[9px] border-t-primary rounded-[7px] border border-secondary bg-white min-w-[230px] h-[168px] flex justify-center items-center flex-col transition-all duration-300 ease-in-out cursor-pointer hover:shadow-custom-button hover:-translate-y-2.5"
						// onClick={() => navigateWithLanguage(getNavigationPath('monitoring/offers'))}
					>
						<h5 className="text-dark text-center roboto-bold text-[16px] leading-[140%] mb-[7px]">
							{t('monitoring')}
						</h5>
						<ul className="list-disc list-inside">
							<li className="text-secondary  roboto-regular text-[16px] leading-[140%]">
								{`${t('requests')} (${analytics?.services?.monitoring.requests || 0})`}
							</li>
							<li className="text-secondary  roboto-regular text-[16px] leading-[140%]">
								{`${t('offer')} (${analytics?.services?.monitoring.offers || 0})`}
							</li>
						</ul>
					</div>
					<Button
						variant="contained"
						onClick={() =>
							navigateWithLanguage(
								`/${isFarmerPath ? 'farmer' : 'specialist'}/monitoring/ongoing-projects`
							)
						}
					>
						{t('ongoingMonitoring')}
						 <span className="ml-[4px]">({analytics?.services?.monitoring.active || 0})</span>
						<KeyboardArrowRightIcon />
					</Button>
				</div>
				<div
					className="border-t-[9px] border-t-primary rounded-[7px] border border-secondary bg-white min-w-[230px] h-[230px] flex justify-center items-center flex-col transition-all duration-300 ease-in-out cursor-pointer hover:shadow-custom-button hover:-translate-y-2.5"
					onClick={handleExpressClick}
				>
					{expressLoading ? (
						<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
							<CircularProgress size={30} sx={{ color: '#3da08f', mb: 2 }} />
							<span className="text-secondary roboto-regular text-[16px]">
								{t('loading')}...
							</span>
						</Box>
					) : (
						<>
							<h5 className="text-dark text-center roboto-bold text-[16px] leading-[140%] mb-[7px]">
								{t('express')}
							</h5>
							<ul className="list-disc list-inside">
								<li className="text-secondary  roboto-regular text-[16px] leading-[140%]">
									{`${t('requests')} (${analytics?.services?.express.requests || 0})`}
								</li>
								<li className="text-secondary  roboto-regular text-[16px] leading-[140%]">
									{`${t('offer')} (${analytics?.services?.express.offers || 0})`}
								</li>
								<li className="text-secondary  roboto-regular text-[16px] leading-[140%]">
									{`${t('ongoing')} (${analytics?.services?.express.active || 0})`}
								</li>
							</ul>
						</>
					)}
				</div>
				<div
					className="border-t-[9px] border-t-primary rounded-[7px] border border-secondary bg-white min-w-[230px] h-[230px] flex justify-center items-center flex-col transition-all duration-300 ease-in-out cursor-pointer hover:shadow-custom-button hover:-translate-y-2.5"
					onClick={() => navigateWithLanguage(getNavigationPath('seasonal-onetime/offers'))}
				>
					<h5 className="text-dark text-center roboto-bold text-[16px] leading-[140%] mb-[7px]">
						{t('requestsAndOffers.oneTimeSeasonal')}
					</h5>
					<ul className="list-disc list-inside">
						<li className="text-secondary  roboto-regular text-[16px] leading-[140%]">
							{`${t('requests')} (${analytics?.services?.seasonal_onetime.requests || 0})`}
						</li>
						<li className="text-secondary  roboto-regular text-[16px] leading-[140%]">
							{`${t('offer')} (${analytics?.services?.seasonal_onetime.offers || 0})`}
						</li>
						<li className="text-secondary  roboto-regular text-[16px] leading-[140%]">
							{`${t('ongoing')} (${analytics?.services?.seasonal_onetime.active || 0})`}
						</li>
					</ul>
				</div>
			</div>
		</div>
	);
};
